/**
 * Bot Game Controller
 *
 * Runs on the human player's client to control the bot during a multiplayer game.
 * Handles king selection, piece placement, and AI move execution.
 *
 * Bot games run locally in Mode.AI — no per-move Firebase syncing.
 * ELO changes and game records are written once at game end by Multiplayer.tsx.
 */

import { ZodiacEngine } from '../zodiacEngine/zodiacEngine';
import { GameState, IPiece, PieceType, State } from '../zodiacEngine/types';
import { AIOrchestrator } from '../zodiacEngine/AI/Core/AIOrchestrator';
import { AIPlayerType } from '../zodiacEngine/AI/Core/IAIPlayer';
import {
  TimeFrame,
  AI_MODEL_CONFIG,
  getTimeLimitFromFrame,
  getBaseAITypeFromModel,
} from '../zodiacEngine/AI/Core/AIVariants';
import { MinimaxAI } from '../zodiacEngine/AI/MinimaxAI';
import { BotConfig } from './types';
import TimeControlManager from '../timeControl/TimeControlManager';
import { ExperienceWeightedSelector } from './pieceStats/ExperienceWeightedSelector';
import { PieceStatsCache } from './pieceStats/PieceStatsCache';
import { ZodiacFEN } from '../zodiacEngine/Fen/ZodiacFEN';

export class BotGameController {
  private engine: ZodiacEngine;
  private aiOrchestrator: AIOrchestrator;
  private botConfig: BotConfig;
  private botPlayerId: string;
  private botIsLight: boolean = false;
  private turnCheckInterval: ReturnType<typeof setInterval> | null = null;
  private isProcessingMove: boolean = false;
  private isSelectingKing: boolean = false;
  private destroyed: boolean = false;

  constructor(engine: ZodiacEngine, botPlayerId: string, botConfig: BotConfig) {
    this.engine = engine;
    this.botPlayerId = botPlayerId;
    this.botConfig = botConfig;
    this.aiOrchestrator = new AIOrchestrator(engine);
  }

  /**
   * Initialize the bot controller.
   * Must be called after game state has both players set.
   */
  initialize(gameState: GameState): void {
    this.botIsLight = gameState.lightPlayer === this.botPlayerId;

    // Configure the AI orchestrator - set the bot's side to the correct AI type
    const aiType = getBaseAITypeFromModel(this.botConfig.aiModel);

    if (this.botIsLight) {
      this.aiOrchestrator.setLightPlayer(aiType);
      this.aiOrchestrator.setDarkPlayer(AIPlayerType.PLAYER);
    } else {
      this.aiOrchestrator.setDarkPlayer(aiType);
      this.aiOrchestrator.setLightPlayer(AIPlayerType.PLAYER);
    }

    // Configure minimax model if needed
    const modelConfig = AI_MODEL_CONFIG[this.botConfig.aiModel];
    if (modelConfig.minimaxModel) {
      const minimaxAI = this.aiOrchestrator.getAI(AIPlayerType.MINIMAX) as MinimaxAI;
      if (minimaxAI) {
        minimaxAI.setModel(modelConfig.minimaxModel);
      }
    }

    // Start turn polling - checks every 2 seconds if it's the bot's turn
    // This is a safety net independent of React effects, which can have timing issues
    this.startTurnPolling();

  }

  /**
   * Handle king selection phase.
   * Picks a random king from the bot's preferred kings.
   */
  async handleKingSelection(gameState: GameState): Promise<void> {
    if (this.destroyed) return;
    if (this.isSelectingKing) return;

    // Check if bot already has a king selected
    const botKing = this.botIsLight ? gameState.lightKing : gameState.darkKing;
    if (botKing) return;

    this.isSelectingKing = true;
    try {
      // Small delay for realism
      await this.delay(800 + Math.random() * 1200);
      if (this.destroyed) return;

      // Re-check engine state after delay (may have changed during await)
      const currentState = this.engine.getGameState();
      if (currentState.state !== State.KingSelection) return;
      const currentBotKing = this.botIsLight ? currentState.lightKing : currentState.darkKing;
      if (currentBotKing) return;

      // Pick king using experience-weighted selection (falls back to random if no data)
      const kings = this.botConfig.preferredKings;
      const selectedKing = await ExperienceWeightedSelector.selectKingForAI(
        kings,
        this.botConfig.aiModel,
        this.botConfig.bannedPieces
      );

      this.engine.setKing(selectedKing, this.botIsLight);
    } finally {
      this.isSelectingKing = false;
    }
  }

  /**
   * Handle piece placement phase.
   * Places a piece on a random valid column on the bot's starting row.
   */
  async handlePiecePlacement(gameState: GameState): Promise<void> {
    if (this.destroyed || this.isProcessingMove) return;

    // Check if it's the bot's turn to place
    const isBotTurn = this.botIsLight === gameState.isLightTurn;
    if (!isBotTurn) return;

    this.isProcessingMove = true;

    try {
      // Small delay for realism
      await this.delay(200 + Math.random() * 300);
      if (this.destroyed) { this.isProcessingMove = false; return; }

      // Re-check engine state after delay (may have changed during await)
      const currentState = this.engine.getGameState();
      if (currentState.state !== State.PieceSelection) return;

      // Determine which pieces the bot still needs to place
      const placedPieceTypes = new Set<PieceType>();
      const startRow = this.botIsLight ? 0 : 7;

      for (let col = 0; col < 5; col++) {
        const square = currentState.board[startRow]?.[col];
        if (square?.Pieces) {
          for (const piece of square.Pieces) {
            if (piece.isLight === this.botIsLight) {
              placedPieceTypes.add(piece.type);
            }
          }
        }
      }

      // Bot has placed all 5 pieces (4 non-king + king itself)
      if (placedPieceTypes.size >= 5) {
        this.isProcessingMove = false;
        return;
      }

      // Get the king type (already selected)
      const kingType = this.botIsLight ? currentState.lightKing : currentState.darkKing;
      if (!kingType) {
        this.isProcessingMove = false;
        return;
      }

      // All 13 piece types, excluding any banned pieces for this bot
      const banned = new Set(this.botConfig.bannedPieces ?? []);
      const allPieces = Object.values(PieceType).filter(pt => !banned.has(pt));

      // Build list of pieces bot can still place (excluding already placed and king which goes last)
      const remainingPieces = allPieces.filter(
        pt => pt !== kingType && !placedPieceTypes.has(pt)
      );

      // If we have 4 non-king pieces placed, place the king last
      let pieceToPlace: PieceType;
      if (placedPieceTypes.size === 4 && !placedPieceTypes.has(kingType)) {
        pieceToPlace = kingType;
      } else if (remainingPieces.length > 0) {
        // Experience-weighted piece selection (falls back to random if no data)
        const alreadyPlaced = [...placedPieceTypes];
        pieceToPlace = await ExperienceWeightedSelector.selectPieceForAI(
          remainingPieces,
          alreadyPlaced,
          this.botConfig.aiModel,
          this.botConfig.bannedPieces
        );
      } else {
        this.isProcessingMove = false;
        return;
      }

      // Find empty columns on starting row
      const emptyColumns: number[] = [];
      for (let col = 0; col < 5; col++) {
        const square = currentState.board[startRow]?.[col];
        const hasBotPiece = square?.Pieces?.some(p => p.isLight === this.botIsLight);
        if (!hasBotPiece) {
          emptyColumns.push(col);
        }
      }

      if (emptyColumns.length === 0) {
        this.isProcessingMove = false;
        return;
      }

      // Experience-weighted column selection
      let statsForColumn: import('./pieceStats/PieceStatsTypes').AIModelPieceStatsDoc | null = null;
      try {
        const cache = PieceStatsCache.getInstance();
        if (cache.hasCachedStats(this.botConfig.aiModel)) {
          statsForColumn = await cache.getStats(this.botConfig.aiModel);
        }
      } catch { /* fall back to random column */ }

      const col = ExperienceWeightedSelector.selectColumn(
        pieceToPlace,
        emptyColumns,
        statsForColumn
      );

      this.engine.setStartingPiece(pieceToPlace, this.botIsLight, col);
    } catch (error) {
    } finally {
      this.isProcessingMove = false;
    }
  }

  /**
   * Handle the bot's turn during gameplay.
   * Runs AI to select and execute a move.
   *
   * Since bot games run in Mode.AI, piece IDs are stable (no Firebase
   * round-trip to regenerate them). We look up pieces by ID directly.
   */
  async handleTurnChange(gameState: GameState): Promise<void> {
    if (this.destroyed || this.isProcessingMove) return;
    if (gameState.state !== State.Playing) return;

    // Check if it's the bot's turn
    const isBotTurn = this.isBotsTurn(gameState);
    if (!isBotTurn) return;

    this.isProcessingMove = true;

    try {
      // Small delay for realism
      await this.delay(300);
      if (this.destroyed) return;

      const timeLimit = getTimeLimitFromFrame(this.botConfig.timeFrame as TimeFrame);
      const startTime = Date.now();

      // Always use the engine's current internal state (not potentially stale React state)
      const currentState = (this.engine as any).gameStateManager.getGameState();

      // Request AI move against current engine state
      const moveResult = await this.aiOrchestrator.requestAIMove(currentState, timeLimit);

      if (this.destroyed || !moveResult) {
        if (!moveResult && !this.destroyed) {
        }
        return;
      }

      const thinkingTime = Date.now() - startTime;

      // Deduct thinking time from bot's clock
      const botSide = this.botIsLight ? 'light' : 'dark';
      TimeControlManager.deductTime(botSide, thinkingTime);

      // Look up the piece by ID — IDs are stable in Mode.AI (no Firebase round-trip)
      const fromSquare = currentState.board[moveResult.fromPosition.row]?.[moveResult.fromPosition.col];
      const piece = fromSquare?.Pieces?.find((p: IPiece) => p.id === moveResult.move.pieceId);

      if (!piece) {
        return;
      }

      // --- Detailed AI diagnostics (FEN before move for reproduction) ---
      const beforeFEN = ZodiacFEN.encode(currentState);

      // Execute the move (makeMove calls sendUpdateToRemote which no-ops in Mode.AI)
      await this.engine.makeMove(
        moveResult.fromPosition,
        moveResult.move.position,
        piece,
        moveResult.move.toPiece,
        moveResult.move.moveType,
        moveResult.move.sideEffects
      );
      const fromR = moveResult.fromPosition.row, fromC = moveResult.fromPosition.col;
      const toR = moveResult.move.position.row, toC = moveResult.move.position.col;
      const cols = 'ABCDE';
      const notation = (r: number, c: number) => `${cols[c]}${r + 1}`;


      if (moveResult.reasoning) {
      }

      // Log principal variation (best line)
      if (moveResult.principalVariation && moveResult.principalVariation.length > 0) {
        const pvLine = moveResult.principalVariation.map((pv: any, i: number) => {
          const side = i % 2 === 0
            ? (this.botIsLight ? 'Light' : 'Dark')
            : (this.botIsLight ? 'Dark' : 'Light');
          const from = pv.fromPosition ? notation(pv.fromPosition.row, pv.fromPosition.col) : '?';
          const to = pv.move?.position ? notation(pv.move.position.row, pv.move.position.col) : '?';
          const pieceType = pv.pieceType || pv.move?.pieceType || '?';
          return `${i + 1}. ${side} ${pieceType} ${from}→${to}`;
        }).join(' | ');
      }

      // Log root move scores (top 10)
      if (moveResult.rootMoveScores && moveResult.rootMoveScores.length > 0) {
        const top = moveResult.rootMoveScores.slice(0, 10);
        top.forEach((ms: { description: string; score: number }, i: number) => {
        });
      }
    } catch (error) {
    } finally {
      this.isProcessingMove = false;
    }
  }

  /**
   * Check if it's the bot's turn, handling bonus turns correctly.
   */
  private isBotsTurn(gameState: GameState): boolean {
    // Handle bonus turns
    if (gameState.skipTurnChange && gameState.activeBonusTurnPieceId) {
      // Find the bonus turn piece to check its side
      for (const row of gameState.board) {
        for (const square of row) {
          for (const piece of square.Pieces) {
            if (piece.id === gameState.activeBonusTurnPieceId) {
              return piece.isLight === this.botIsLight;
            }
          }
        }
      }
    }

    return gameState.isLightTurn === this.botIsLight;
  }

  /**
   * Periodically check if it's the bot's turn and trigger a move.
   * This is independent of React effects and ensures the bot always responds,
   * even if React effect timing misses the turn change.
   */
  private startTurnPolling(): void {
    this.turnCheckInterval = setInterval(() => {
      if (this.destroyed || this.isProcessingMove) return;

      const gameState = this.engine.getGameState();
      if (gameState.state !== State.Playing) return;

      const isBotTurn = this.isBotsTurn(gameState);
      if (!isBotTurn) return;

      this.handleTurnChange(gameState);
    }, 2000);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    this.destroyed = true;
    this.isSelectingKing = false;
    if (this.turnCheckInterval) {
      clearInterval(this.turnCheckInterval);
      this.turnCheckInterval = null;
    }
  }
}
